import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { legacyFileUrl } from '../lib/format';

/**
 * The receipt/photo lightbox — a reduction of the old `ImageUploadGallery` to
 * the read-only path the delivery screens actually used (the sortable-upload
 * machinery belonged to profile screens that are not part of legacy mode).
 *
 * Renders nothing while `images` is empty; the callers hold `string[] | null`
 * state and open it by setting the paths, exactly like the old
 * `setUploadedImages([receipt_url])` pattern.
 *
 * The caption is hardcoded Russian ("Фото продукта: N из M") because the old
 * component hardcoded it too.
 */
export function LegacyImageLightbox({
  images,
  onClose,
}: {
  images: string[] | null;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);

  if (!images || images.length === 0) return null;
  const count = Math.min(index, images.length - 1);
  const src = legacyFileUrl(images[count] ?? '');

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-away, as in the source; the close affordance for keyboards is the buttons below
    <div
      data-legacy
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div className="relative flex flex-col items-center">
        <p className="mb-10 text-[24px] font-medium text-white">
          <span className="text-[16px] font-medium">Фото продукта: </span>
          {count + 1} <span className="text-[16px] font-medium">из </span>
          {images.length}
        </p>
        <div className="h-[560px] w-[560px] max-w-[90vw] select-none">
          {src ? (
            <img
              src={src}
              alt="Фото заказа"
              className="h-full w-full rounded-[16px] object-contain"
            />
          ) : null}
        </div>
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Предыдущее фото"
              onClick={() =>
                setIndex((prev) => (prev <= 0 ? images.length - 1 : prev - 1))
              }
              className="absolute bottom-1/2 -left-[100px] flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-white hover:bg-white/10"
            >
              <ChevronLeftIcon className="size-6" />
            </button>
            <button
              type="button"
              aria-label="Следующее фото"
              onClick={() =>
                setIndex((prev) => (prev + 1 >= images.length ? 0 : prev + 1))
              }
              className="absolute -right-[100px] bottom-1/2 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-white hover:bg-white/10"
            >
              <ChevronRightIcon className="size-6" />
            </button>
          </>
        )}
        {/*
          The source closed only via backdrop click. Kept visually identical:
          this control is invisible until keyboard-focused, purely so the
          overlay is escapable without a pointer.
        */}
        <button
          type="button"
          onClick={onClose}
          className="sr-only rounded-[96px] focus:not-sr-only focus:mt-6 focus:px-5 focus:py-2 focus:text-white"
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
